import { useEffect } from 'react'
import { dynamicGa4DataLayerPush } from '@which/shared'

import { Layout } from '../../layouts'
import type { RecommendedContentType, RecommendedModelType } from '../../models'
import { RecommendationsImageLinksLayout } from '../layouts/ImageLinks/RecommendationsImageLinksLayout'
import { RecommendationsTextLinksLayout } from '../layouts/TextLinks/RecommendationsTextLinksLayout'
import { RecommendationsTwoByTwoLayout } from '../layouts/TwoByTwo/RecommendationsTwoByTwoLayout'

type RecommendationsProps = {
  recommendations: RecommendedContentType[]
  whichId: string
  modelId: string
  layout: Layout
  position?: string
  sectionTitle?: string
  enableConsentBanner?: boolean
}

const Recommendations = ({
  recommendations,
  whichId,
  modelId,
  layout,
  position,
  sectionTitle = 'Based on your interests',
  enableConsentBanner = false,
}: RecommendationsProps) => {
  useEffect(() => {
    dynamicGa4DataLayerPush({
      event: 'notification_ab_test',
      item_text: `${recommendations.length}`,
      item_parent_text: modelId,
      item_group: 'R4U Recommendation Count',
    })
  }, [recommendations, modelId])

  switch (layout) {
    case Layout.TwoByTwo:
      return (
        <RecommendationsTwoByTwoLayout
          recommendations={recommendations}
          title={sectionTitle}
          whichId={whichId}
          modelId={modelId}
          enableConsentBanner={enableConsentBanner}
        />
      )

    case Layout.TextLinks:
      return (
        <RecommendationsTextLinksLayout
          recommendations={recommendations}
          title={sectionTitle}
          whichId={whichId}
          modelId={modelId}
          position={position}
        />
      )

    case Layout.ImageLinks:
      return (
        <RecommendationsImageLinksLayout
          recommendations={recommendations}
          title={sectionTitle}
          whichId={whichId}
          modelId={modelId}
          position={position}
        />
      )

    default:
      console.warn(`The ${layout} is not defined for <Recommendations ... />`)
      return null
  }
}

interface ModelRecommendationsProps {
  model: RecommendedModelType | null
  whichId: string
  layout: Layout
  position?: string
  sectionTitle?: string
  enableConsentBanner?: boolean
}

export const ModelRecommendations = ({
  model,
  whichId,
  layout,
  position,
  sectionTitle,
  enableConsentBanner = false,
}: ModelRecommendationsProps) => {
  const { content, model: modelId } = model ?? {}

  if (modelId && content && content.length > 0) {
    return (
      <Recommendations
        recommendations={content}
        whichId={whichId}
        modelId={modelId}
        layout={layout}
        position={position}
        sectionTitle={sectionTitle}
        enableConsentBanner={enableConsentBanner}
      />
    )
  }

  return null
}
